import React from 'react';
import {
  Card, CardContent, Grid, Typography, Button, Box, Chip, Avatar, CircularProgress
} from '@mui/material';
import { MdSportsScore, MdCalculate, MdAccessTime } from 'react-icons/md';
import { LiveMatchTimer } from './LiveMatchTimer';

interface LiveOpportunity {
  id: string;
  matchId: string;
  homeTeamName: string;
  awayTeamName: string;
  homeTeamLogo?: string;
  awayTeamLogo?: string;
  league: {
    name: string;
    id: string;
    country?: string;
    logo?: string;
  };
  matchDateTime: string;
  matchStatus: string;
  currentScore: {
    home: number;
    away: number;
  };
  halftimeScore: {
    home: number;
    away: number;
  };
  betType: string;
  betOption: string;
  odds: string | number;
  confidenceScore: number;
  valueAssessment: number;
  impliedProbability: string | number;
  source: string;
  reasons: string[];
  riskLevel: string;
  isLive: boolean;
  predictionMethod: string;
  verificationStatus: string;
  tracked: boolean;
  finished?: boolean;
  matchDetails: {
    minute: number;
    homeFormation: string;
    awayFormation: string;
    referee: string;
    stadium: string;
    round: string;
  };
  statistics: {
    possession: { home: string; away: string };
    shots: { home: string; away: string };
    shotsOnTarget: { home: string; away: string };
    corners: { home: string; away: string };
    fouls: { home: string; away: string };
    yellowCards: { home: string; away: string };
    redCards: { home: string; away: string };
  };
  recentGoals: Array<{
    time: string;
    scorer: string;
    team: 'home' | 'away';
    score: string;
  }>;
}

interface OpportunityCardProps {
  opportunity: LiveOpportunity;
  onViewDetails: (matchId: string) => void;
  onAddToTicket: (opportunity: LiveOpportunity) => void;
  loadingOpportunities: boolean;
  addingTicket: string | null;
  loadingDetails?: string | null;
}

export function OpportunityCard({ 
  opportunity, 
  onViewDetails, 
  onAddToTicket, 
  loadingOpportunities, 
  addingTicket,
  loadingDetails
}: OpportunityCardProps) {
  return (
    <Grid item xs={12} md={4} key={opportunity.id}>
      <Card 
        sx={{ 
          height: '100%',
          position: 'relative', 
          overflow: 'visible',
          background: '#1d3a3600',
          border: '1px solid rgba(73, 251, 176, 0.2)',
          borderRadius: '12px',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(73, 251, 176, 0.15)',
            transition: 'all 0.3s ease',
            borderColor: 'rgba(73, 251, 176, 0.4)'
          }
        }}
      >
        {opportunity.isLive && (
          <Chip
            label="🔴 AO VIVO"
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              backgroundColor: '#ff4444',
              color: 'white',
              fontWeight: 'bold',
              zIndex: 1,
              animation: 'pulse 2s infinite'
            }}
          />
        )}

        <CardContent sx={{ p: 3 }}>
          {/* Header do jogo */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="caption" sx={{ color: '#49fbb0', fontWeight: 600 }}>
              {opportunity.league.name} • {opportunity.league.country}
            </Typography>
            <LiveMatchTimer 
              matchStatus={opportunity.matchStatus}
              isLive={opportunity.isLive}
              size="small"
            />
          </Box>



          {/* Times */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <Avatar 
                src={opportunity.homeTeamLogo} 
                sx={{ width: 32, height: 32, mr: 1 }}
              >
                {opportunity.homeTeamName.charAt(0)}
              </Avatar>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {opportunity.homeTeamName}
              </Typography>
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              px: 2,
              py: 1,
              backgroundColor: '#49fbb0',
              borderRadius: '8px',
              minWidth: '60px',
              justifyContent: 'center'
            }}>
              <Typography variant="h6" sx={{ color: '#183530', fontWeight: 'bold' }}>
                {opportunity.currentScore.home} - {opportunity.currentScore.away}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>
                {opportunity.awayTeamName}
              </Typography>
              <Avatar 
                src={opportunity.awayTeamLogo} 
                sx={{ width: 32, height: 32, ml: 1 }}
              >
                {opportunity.awayTeamName.charAt(0)}
              </Avatar>
            </Box>
          </Box>

          {/* Formações */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Formação Casa: {opportunity.matchDetails.homeFormation}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Formação Fora: {opportunity.matchDetails.awayFormation}
            </Typography>
          </Box>

          {/* Estatísticas ao Vivo */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#49fbb0' }}>
              📊 Estatísticas ao Vivo
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Posse</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {opportunity.statistics.possession.home} - {opportunity.statistics.possession.away}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Chutes</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {opportunity.statistics.shots.home} - {opportunity.statistics.shots.away}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Escanteios</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {opportunity.statistics.corners.home} - {opportunity.statistics.corners.away}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Gols Recentes */}
          {opportunity.recentGoals.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#49fbb0' }}>
                ⚽ Gols Recentes
              </Typography>
              {opportunity.recentGoals.slice(0, 2).map((goal: any, index: number) => (
                <Typography key={index} variant="caption" sx={{ 
                  display: 'block', 
                  color: 'text.secondary',
                  mb: 0.5 
                }}>
                  {goal.time}' {goal.scorer} ({goal.score})
                </Typography>
              ))}
            </Box>
          )}

          {/* Análise da Aposta */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#49fbb0' }}>
              🎯 Oportunidade de Aposta
            </Typography>
            <Box sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {opportunity.betType}: {opportunity.betOption}
              </Typography>
              <Typography variant="h4" sx={{ color: '#49fbb0', fontWeight: 'bold' }}>
                @{typeof opportunity.odds === 'string' ? opportunity.odds : opportunity.odds.toFixed(2)}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Chip 
                label={`${opportunity.confidenceScore}% confiança`}
                size="small"
                sx={{ 
                  backgroundColor: opportunity.confidenceScore >= 75 ? '#49fbb0' : 
                                   opportunity.confidenceScore >= 60 ? '#ffa726' : '#ff5722',
                  color: '#183530',
                  fontWeight: 600
                }}
              />
              <Chip 
                label={`Risco ${opportunity.riskLevel}`}
                size="small"
                variant="outlined"
                sx={{ borderColor: '#49fbb0', color: '#49fbb0' }}
              />
              <Chip 
                label={`Valor ${Math.round(opportunity.valueAssessment)}%`}
                size="small"
                variant="outlined"
                sx={{ borderColor: '#49fbb0', color: '#49fbb0' }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {opportunity.reasons.slice(0, 2).join(' • ')}
            </Typography>
          </Box>

          {/* Botões de Ação */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              variant="contained"
              onClick={() => onViewDetails(opportunity.matchId)}
              disabled={loadingDetails === opportunity.matchId}
              startIcon={
                loadingDetails === opportunity.matchId ? 
                  <CircularProgress size={16} sx={{ color: '#183530' }} /> : 
                  <MdSportsScore />
              }
              sx={{
                flex: 1,
                background: 'linear-gradient(135deg, #49fbb0 0%, #36d399 100%)',
                color: '#183530',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '8px',
                '&:hover': {
                  background: 'linear-gradient(135deg, #36d399 0%, #2dd4aa 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(73, 251, 176, 0.3)'
                },
                '&:disabled': {
                  background: 'rgba(73, 251, 176, 0.3)',
                  color: 'rgba(24, 53, 48, 0.5)'
                }
              }}
            >
              {loadingDetails === opportunity.matchId ? 'Carregando...' : 'Ver Oportunidades'}
            </Button>
            
            <Button
              variant="outlined"
              onClick={() => onAddToTicket(opportunity)}
              disabled={addingTicket === opportunity.id}
              startIcon={addingTicket === opportunity.id ? <CircularProgress size={16} /> : <MdCalculate />}
              sx={{
                flex: 1,
                borderColor: '#49fbb0',
                color: '#49fbb0',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '8px',
                '&:hover': {
                  borderColor: '#36d399',
                  backgroundColor: 'rgba(73, 251, 176, 0.1)',
                  transform: 'translateY(-1px)'
                },
                '&:disabled': {
                  borderColor: 'rgba(73, 251, 176, 0.3)',
                  color: 'rgba(73, 251, 176, 0.5)'
                }
              }}
            >
              {addingTicket === opportunity.id ? 'Adicionando...' : 'Adicionar Bilhete'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
} 